import React from 'react';
import MarketClock from './MarketClock';
import { DOMAINS } from '../lib/agents';

// ─────────────────────────────────────────────────────────────────────────────
// DomainBar — domain pills.
//
// Chunk 6 additive edit: accepts `position`, `homePill`, `onHomePress`, `onHome`
// for the new bottom-mount via AppShell. The existing top-mount usage (in the
// fallback chat UI) is unchanged — every new prop is optional and the legacy
// styling is preserved when `position === 'top'` (the default). This honors
// Flag 7 (current desktop styling kept where the existing surface is used) and
// FIX 1 / CORRECTION 1 (existing `onDomainChange` API preserved; AppShell maps
// its `onDomainPress` to this prop at the call site).
//
// Domain rosters now read from lib/agents.ts (Q8 + Flag 7) — single source.
// ─────────────────────────────────────────────────────────────────────────────

// Settings is not in DOMAINS, so it has no entry in ACCENTS. A neutral accent keeps
// it visually a page rather than a tenth domain.
const SETTINGS_ACCENT = '#8aa0b4';

interface DomainBarProps {
  activeDomain: string;
  onDomainChange: (domain: string) => void; // existing API — POSTs /api/domain via App.tsx
  // ── Chunk 6 additive (all optional, no regression on existing top-mount) ──
  position?: 'top' | 'bottom';
  homePill?: boolean;
  onHomePress?: () => void; // UI only (Flag 2) — no backend POST
  onHome?: boolean; // active-state flag when on the homepage (Q3)
  // SETTINGS IS A PAGE ON THIS BAR, NOT A DOMAIN. It is rendered AFTER the DOMAINS
  // map and never enters it, so activeDomain is untouched and no domain conditional
  // anywhere sees a new value. One page, reachable from every domain.
  settingsActive?: boolean;
  onSettingsPress?: () => void;
}

const DomainBar: React.FC<DomainBarProps> = ({
  activeDomain,
  onDomainChange,
  position = 'top',
  homePill,
  onHomePress,
  onHome,
  settingsActive,
  onSettingsPress,
}) => {
  if (position === 'bottom') {
    // ── New iOS-parity bottom mount (AppShell) ────────────────────────────
    // Per-pill accent border + label in domain accent when active. HOMEPAGE
    // pill prepended when `homePill` is set.
    return (
      <div
        className="flex items-center bg-rexy-card/60 backdrop-blur-sm border-t border-rexy-border
          px-3 py-2 gap-2 overflow-x-auto"
      >
        {homePill && (
          <button
            type="button"
            onClick={onHomePress}
            className={`px-4 py-1.5 rounded-full text-[11px] font-bold tracking-widest
              transition-all whitespace-nowrap uppercase border ${
              onHome
                ? 'bg-rexy-text/10 text-rexy-text border-rexy-text/40'
                : 'text-rexy-muted hover:text-rexy-text hover:bg-rexy-border/50 border-transparent'
            }`}
          >
            HOMEPAGE
          </button>
        )}
        {DOMAINS.map((d) => {
          const isActive = activeDomain === d.id;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => onDomainChange(d.id)}
              style={
                isActive
                  ? { borderColor: d.accent, color: d.accent }
                  : undefined
              }
              className={`px-4 py-1.5 rounded-full text-[11px] font-bold tracking-widest
                transition-all whitespace-nowrap uppercase border ${
                isActive
                  ? ''
                  : 'text-rexy-muted hover:text-rexy-text hover:bg-rexy-border/50 border-transparent'
              }`}
            >
              {d.label}
            </button>
          );
        })}
        {onSettingsPress && (
          <button
            type="button"
            onClick={onSettingsPress}
            style={settingsActive ? { borderColor: SETTINGS_ACCENT, color: SETTINGS_ACCENT } : undefined}
            className={`px-4 py-1.5 rounded-full text-[11px] font-bold tracking-widest
              transition-all whitespace-nowrap uppercase border ${
              settingsActive
                ? ''
                : 'text-rexy-muted hover:text-rexy-text hover:bg-rexy-border/50 border-transparent'
            }`}
          >
            SETTINGS
          </button>
        )}
        <MarketClock />
      </div>
    );
  }

  // ── Existing top mount — unchanged styling (Flag 7, no regression) ────────
  return (
    <div
      className="flex items-center bg-rexy-card border-b border-rexy-border
        px-3 py-2 gap-2 overflow-x-auto"
    >
      {DOMAINS.map((d) => (
        <button
          key={d.id}
          onClick={() => onDomainChange(d.id)}
          className={`px-4 py-1.5 rounded text-xs font-bold tracking-widest
            transition-all whitespace-nowrap uppercase ${
            d.id === 'CODE'
              ? activeDomain === d.id
                ? 'bg-purple-900/40 text-purple-400 border border-purple-500/40'
                : 'text-purple-500/60 hover:text-purple-400 hover:bg-purple-900/20'
              : d.id === 'SECURITY'
              ? activeDomain === d.id
                ? 'bg-red-900/40 text-red-400 border border-red-500/40'
                : 'text-red-500/60 hover:text-red-400 hover:bg-red-900/20'
              : activeDomain === d.id
              ? 'bg-rexy-border text-rexy-text border border-rexy-text/20'
              : 'text-rexy-muted hover:text-rexy-text hover:bg-rexy-border/50'
          }`}
        >
          {d.label}
        </button>
      ))}
      {onSettingsPress && (
        <button
          type="button"
          onClick={onSettingsPress}
          className={`px-4 py-1.5 rounded text-xs font-bold tracking-widest
            transition-all whitespace-nowrap uppercase ${
            settingsActive
              ? 'bg-rexy-border text-rexy-text border border-rexy-text/20'
              : 'text-rexy-muted hover:text-rexy-text hover:bg-rexy-border/50'
          }`}
        >
          SETTINGS
        </button>
      )}
      <MarketClock />
    </div>
  );
};

export default DomainBar;
